import { DataTable, DataTableSortStatus } from "mantine-datatable";
import { Stack, Tooltip, Text, Flex, Box } from "@mantine/core";
import { TextCell, TextCellSkeleton } from "../components/TableCells/TextCell";
import { OwnerCell, OwnerCellSkeleton } from "../components/TableCells/OwnerCell";
import { PriorityCell, PriorityCellSkeleton } from "../components/TableCells/PriorityCell";
import { TemplateCell, TemplateCellSkeleton } from "../components/TableCells/TemplateCell";
import { CommitmentsCell, CommitmentsCellSkeleton } from "../components/TableCells/CommitmentsCell";
import { DynamicCell, DynamicCellSkeleton } from "../components/TableCells/DynamicCell";
import { updateAccessorByState } from "../../../utils";
import React, { useContext, useEffect, useMemo, useState } from "react";
import SyncContext from "../../../context/SyncContext";
// import IdeasContext from "../IdeasContext"; // Removed - using props instead
import { useNavigate } from "react-router-dom";
import StarCell, { StarCellSkeleton } from "../components/TableCells/StarCell";
import NewRequestsCell, { NewRequestsCellSkeleton } from "../components/TableCells/NewRequestsCell";
import { TagsCell, TagsCellSkeleton } from "../components/TableCells/TagsCell";
import { CustomersCell, CustomersCellSkeleton } from "../components/TableCells/CustomersCell";
import { IconArrowDown } from "@tabler/icons";
import { FlippedArrow } from "./FlippedArrow";
import { useHover } from "@mantine/hooks";
import { DeliveryStatusCell, DeliveryStatusCellSkeleton } from "../components/TableCells/DeliveryStatusCell";
import { BarCell, BarCellSkeleton } from "../components/TableCells/bar/BarCell";
import { calcTopValues, parseFiltersFromQuery } from "../ideasHelpers";
import FiltersContext from "../../ideas/filters/context/FiltersContext";

export default function IdeasTable({
  ideaColumns,
  ideas,
  isAllowedToEditDates,
  handlePageChange,
  totalRecords,
  recordsPerPage,
  page,
  sortStatus,
  setSortStatus,
  loading,
  PAGE_SIZE,
  selectedView,
  isTagsHovered = false,
  sortState = "total",
}: {
  ideaColumns: any[];
  ideas: any[];
  isAllowedToEditDates: boolean;
  handlePageChange: (newPage: string) => void;
  totalRecords: number | undefined;
  recordsPerPage: number;
  page: number;
  sortStatus: DataTableSortStatus;
  setSortStatus: React.Dispatch<React.SetStateAction<DataTableSortStatus>>;
  loading: boolean;
  PAGE_SIZE: number;
  selectedView?: any;
  isTagsHovered?: boolean;
  sortState?: string;
}) {
  const {
    requestPriorities,
    ideaPriorities,
    displayState,
    ideasTableDisplay,
    customEvidenceObj,
    generatedEnabled
  } = useContext(SyncContext);

  const { subPageKey } = useContext(FiltersContext);
  
  const [columns, setColumns] = useState<any[]>([]);
  
  // Debug logging (development only)
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 IdeasTable received props:', {
      ideaColumns: ideaColumns?.length || 0,
      ideas: ideas?.length || 0,
      selectedView: selectedView?.name || 'none'
    });
  }

  const { ref } = useHover();

  const [dynamicSxObj, setDynamicSxObj] = useState<any>();

  const navigate = useNavigate();
  const cellComponents: { [key: string]: React.FC<any> } = {
    TextCell,
    OwnerCell,
    PriorityCell,
    TemplateCell,
    CommitmentsCell,
    StarCell,
    DynamicCell,
    NewRequestsCell,
    TagsCell,
    DeliveryStatusCell,
    BarCell,
    CustomersCell,
  };

  // Skeleton components mapping
  const skeletonComponents: { [key: string]: React.FC<any> } = {
    TextCell: TextCellSkeleton,
    OwnerCell: OwnerCellSkeleton,
    PriorityCell: PriorityCellSkeleton,
    TemplateCell: TemplateCellSkeleton,
    CommitmentsCell: CommitmentsCellSkeleton,
    StarCell: StarCellSkeleton,
    DynamicCell: DynamicCellSkeleton,
    NewRequestsCell: NewRequestsCellSkeleton,
    TagsCell: TagsCellSkeleton,
    DeliveryStatusCell: DeliveryStatusCellSkeleton,
    BarCell: BarCellSkeleton,
    CustomersCell: CustomersCellSkeleton,
  };

  const dealBreakerObj = requestPriorities.find(
    (r: any) => r.key === "Deal Breaker"
  ) || {
    key: "Deal Breaker",
    label: "Deal Breaker",
  };

  const handleOnRowClick = (ideaId: string) => {
    // Get the current query parameters.
    const currentParams = new URLSearchParams(location.search);
    // Create a new URLSearchParams instance to hold only evidence filters.
    const evidenceFilters = new URLSearchParams();
  
    // Iterate over all current query parameters.
    currentParams.forEach((value, key) => {
      if (key.startsWith("filter.evidence")) {
        evidenceFilters.set(key, value);
      }
    });
  
    // Build the new URL, adding the evidence filters if any exist.
    const queryString = evidenceFilters.toString();
    const url = `/idea/${ideaId}${queryString ? `?${queryString}` : ""}`;
  
    // Save current filters in sessionStorage.
    sessionStorage.setItem("lastFilters", `?${currentParams.toString()}`);
    if(subPageKey){
      sessionStorage.setItem("subPageKey", `${subPageKey}`);
    } else {
      sessionStorage.removeItem("subPageKey");
    }
  
    // Navigate to the new URL.
    navigate(url);
  };

  const onSortStatusChanged = (sortStatus: DataTableSortStatus) => {
    setSortStatus((prevStatus) => {
      if (prevStatus?.columnAccessor === sortStatus.columnAccessor) {
        return sortStatus;
      } else {
        return { columnAccessor: sortStatus.columnAccessor, direction: "asc" };
      }
    });
  };

  const draggIt = (draggedTitle: string, droppedTitle: string) => {
    setColumns((prevColumns) => {
      const draggedIndex = prevColumns.findIndex(
        (col) => col.title === draggedTitle
      );
      const droppedIndex = prevColumns.findIndex(
        (col) => col.title === droppedTitle
      );

      if (
        draggedIndex === -1 ||
        droppedIndex === -1 ||
        draggedIndex === droppedIndex
      ) {
        // No valid reordering required
        return prevColumns;
      }

      // Extract the dragged column
      const draggedColumn = prevColumns[draggedIndex];

      // Create a new array excluding the dragged column
      const filteredColumns = prevColumns.filter(
        (_, index) => index !== draggedIndex
      );

      // Insert the dragged column at the dropped position
      const splicedCols = [
        ...filteredColumns.slice(0, droppedIndex),
        draggedColumn,
        ...filteredColumns.slice(droppedIndex),
      ].map((col, index) => ({
        ...col,
        index, // Reassign the correct index
      }));

      return splicedCols;
    });
  };

  const handleOnDrag = (e: React.DragEvent, draggedColTitle: string) => {
    e.dataTransfer.setData("title", draggedColTitle);
  };

  const handleOnDrop = (e: React.DragEvent, droppedColTitle: string) => {
    const draggedCol = e.dataTransfer.getData("title") as string;

    draggIt(draggedCol, droppedColTitle);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Setting columns in IdeasTable:', ideaColumns?.length || 0);
    }
    setColumns(ideaColumns || []);
  }, [ideaColumns]);

  const cols = useMemo(
    () => calcTopValues(ideas, columns),
    [ideaColumns, sortState, ideas, sortStatus, columns]
  );

  useEffect(() => {
    const newSxObj: any = {};

    columns.forEach((colItem: any, index: number) => {
      const colBackground = colItem.customBackground;
      if (colBackground) {
        newSxObj[`tbody td:nth-of-type(${index + 1})`] = {
          backgroundColor: colBackground,
          "&:hover": {
            backgroundColor: "#EFEFFD !important",
          },
        };
      }
    });

    setDynamicSxObj(newSxObj);
  }, [columns, ideaColumns]);

  // Generate skeleton data using the same filtered columns as DataTable
  const skeletonData = useMemo(() => {
    if (!loading) return [];
    
    // Simple skeleton data - cells will be rendered as skeletons in the render method
    return Array.from({ length: 25 }, (_, index) => ({
      _id: `skeleton-${index}`,
      __isSkeletonRow: true, // Flag to identify skeleton rows
    }));
  }, [loading]);

  // Debug logging (development only)
  if (process.env.NODE_ENV === 'development') {
    console.log('📋 IdeasTable Debug:', {
      totalColumns: cols.length,
      enabledColumns: cols.filter((col: any) => col.enabled && !col.hidden).length,
      sortState,
      ideas: ideas.length,
      loading
    });
    
    // Log column details
    const enabledCols = cols.filter((col: any) => col.enabled && !col.hidden);
    console.log('📋 Enabled columns:', enabledCols.map((col: any) => ({
      title: col.title,
      accessor: col.accessor,
      renderComponent: col.renderComponent,
      enabled: col.enabled,
      hidden: col.hidden
    })));
  }

  return (
    <DataTable
      pinLastColumn
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        thead: {
          position: "sticky",
          top: 0,
          zIndex: 4,
          backgroundColor: "#F3F4F6",
        },
        "thead tr:first-of-type": {
          borderColor: "#dee2e6",
          fontSize: 12,
        },
        "thead th": {
          height: "48px",
          /* padding: "6px 10px !important", */
          fontSize: 12,
          textAlign: "left",
          verticalAlign: "middle",
          backgroundColor: "#EFEFFD",
          "&:hover": {
            backgroundColor: "#EFEFFD !important",
          },
        },
        "tbody td": {
          height: "48px",
          fontSize: 12,
        },
        ".external-link-icon": {
          display: "none",
          opacity: 0,
          transition: "opacity 0.2s ease",
        },
        "tbody tr:hover .external-link-title": {
          opacity: 1,
          display: "flex",
        },
        ".external-link-title": {
          display: "none",
        },
        ".text-cell-title": {
          position: "relative",
        },
        "thead th:first-of-type": {
          position: "sticky",
          left: 0,
          zIndex: 5,
          backgroundColor: "#EFEFFD",
          borderRight: "1px solid #dee2e6",
        },
        "thead th:last-of-type": {
          position: "sticky",
          right: 0,
          zIndex: 5,
          backgroundColor: "#EFEFFD",
          borderLeft: "1px solid #dee2e6 !important",
        },
        "tbody td:first-of-type": {
          position: "sticky",
          left: 0,
          zIndex: 3,
          backgroundColor: "white",
          borderRight: "1px solid #dee2e6",
          "&:hover": {
            backgroundColor: "#EFEFFD !important",
          },
        },

        "tbody tr[data-state='generated'] td:first-of-type": {
          backgroundColor: selectedView?.name === 'Bagel AI' ? 'white' : "#FFFCE6",
        },
        "tbody td:last-of-type": {
          backgroundColor: "white",
          borderLeft: "1px solid #dee2e6 !important",
          /* "&:hover": {
              backgroundColor: "#dbe8ff !important", 
            }, */
        },
        "tbody tr": {
          "&:hover": {
            backgroundColor: !isTagsHovered
              ? "#EFEFFD !important"
              : "white !important", // Row hover background color
            "& td:first-of-type": {
              backgroundColor: !isTagsHovered
                ? "#EFEFFD !important"
                : "white !important", // First column hover color
            },
            "& td:last-of-type": {
              backgroundColor: !isTagsHovered
                ? "#EFEFFD !important"
                : "white !important", // Last column hover color
            },
            "& td": {
              backgroundColor: "inherit !important", // Ensure all cells follow the row hover style
            },
          },
        },
        ...dynamicSxObj,
      }}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChanged}
      withBorder
      idAccessor={"_id"}
      storeColumnsKey={"Ideas-table"}
      records={loading ? skeletonData : ideas}
      sortIcons={{
        sorted: <FlippedArrow />,
        unsorted: (
          <IconArrowDown
            size={14}
            strokeWidth={2.6}
            style={{
              color: "rgba(92, 92, 235, 0.4)",
              transform: "scale(0.95, 1)",
            }}
          />
        ),
      }}
      columns={[
        ...cols
          .filter((col: any) => col.enabled && !col.hidden)
          .sort((a: any, b: any) => a.index - b.index)
          .map((col: any) => {
            const finalAccessor = col.renderComponent == "DynamicCell" ||
              col.renderComponent == "BarCell"
              ? updateAccessorByState(col.accessor, sortState)
              : col.accessor;

            // Debug accessor transformation in development
            if (process.env.NODE_ENV === 'development' && 
                (col.renderComponent == "DynamicCell" || col.renderComponent == "BarCell")) {
              console.log(`🔄 Accessor transformed for ${col.title}:`, {
                original: col.accessor,
                final: finalAccessor,
                sortState,
                renderComponent: col.renderComponent
              });
            }

            return {
              ...col,
              draggble: false,
              accessor: finalAccessor,
              title: (
                <div
                  style={{ cursor: "grab" }}
                  className={col.title}
                  draggable
                  onDragStart={(e) => handleOnDrag(e, col?.title)}
                  onDrop={(e) => handleOnDrop(e, col?.title)}
                  onDragOver={handleDragOver}
                >
                  <Flex>
                    <Tooltip label={col?.tooltip} disabled={!col?.tooltip}>
                      {<Text>{col?.title}</Text>}
                    </Tooltip>
                  </Flex>
                </div>
              ),
              width: col.width,
              sortable: col?.sortable,
              openIdeaOnCLick: col?.openIdeaOnCLick,

              resizable: false,
              render: (data: any) => {
                const CellComponent = cellComponents[col.renderComponent]
                  ? cellComponents[col.renderComponent]
                  : cellComponents["TextCell"];
                
                const SkeletonComponent = skeletonComponents[col.renderComponent]
                  ? skeletonComponents[col.renderComponent]
                  : skeletonComponents["TextCell"];

                return (
                  <Box ref={ref}>
                    {data.__isSkeletonRow ? (
                      <SkeletonComponent col={col} />
                    ) : (
                      <CellComponent
                        data={data}
                        col={col}
                        renderParams={col.renderParams || {}}
                        ideaPriorities={ideaPriorities}
                        dealBreakerObj={dealBreakerObj}
                        customEvidenceObj={customEvidenceObj}
                        isAllowedToEditDates={isAllowedToEditDates}
                        ideasTableDisplay={ideasTableDisplay}
                        displayState={displayState}
                        accessor={col.accessor}
                        sortState={sortState}
                      />
                    )}
                  </Box>
                );
              },
            };
          }),
      ]}
      rowStyle={(record: any) => {

        if(selectedView?.name === 'Bagel AI' && generatedEnabled){
          return undefined;
        }
        else if (
          record.state === "generated" &&
            generatedEnabled 
        ) {
          return { backgroundColor: "#FFFCE6" };
        }
        return undefined;
      }}
      customRowAttributes={({ state }) => ({
        "data-state": state
      })}
      height={`calc(100vh - 240px)`}
      totalRecords={totalRecords}
      recordsPerPage={recordsPerPage}
      page={page}
      onPageChange={(p) => handlePageChange(p.toString())}
      onCellClick={({ record: idea, column }: any) => {
        if (
          column?.openIdeaOnCLick === undefined ||
            column?.openIdeaOnCLick
        ) {
          if (column?.renderComponent === "StarCell") {
            return;
          } else {
            handleOnRowClick(idea._id);
          }
        }
      }}
    />
  );
}
